import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { MarketingNav, MarketingFooter } from '../components/marketing/MarketingLayout';
import { 
  Play, ChevronRight, CheckCircle2, ArrowRight, Shield, Building2, 
  Globe, FileText, Users, BarChart3, Lock, QrCode, Calculator,
  Receipt, ClipboardCheck, UserCheck, Landmark, TrendingUp, PieChart,
  Layers, Briefcase, Target
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DUBAI_BG = [
  'https://images.unsplash.com/photo-1773829126358-1e5f4e5aa532?w=1920&q=60',
  'https://images.unsplash.com/photo-1726533765275-a69cfd7f9897?w=1920&q=60',
  'https://images.unsplash.com/photo-1640184713837-02e0d30b0031?w=1920&q=60',
];

// Dynamic floating mockup screens shown around the hero — uses existing booklet captures
const HERO_DYNAMIC_SHOTS = [
  '/booklet/02_dashboard.jpeg',
  '/booklet/06_sales.jpeg',
  '/booklet/09_analytics.jpeg',
  '/booklet/04_payroll.jpeg',
];

/* Counter */
function Counter({ end, suffix = '', prefix = '', label }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let c = 0;
    const inc = end / 125;
    const t = setInterval(() => { c += inc; if (c >= end) { setCount(end); clearInterval(t); } else setCount(Math.floor(c)); }, 16);
    return () => clearInterval(t);
  }, [inView, end]);
  return (
    <div ref={ref} className="text-center" data-testid={`counter-${label?.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#d4a84b]" style={{ fontFamily: "'Outfit', sans-serif" }}>{prefix}{count}{suffix}</div>
      <div className="text-xs sm:text-sm mt-1 text-zinc-400 font-medium">{label}</div>
    </div>
  );
}

/* Floating animated mockup screens — adds dynamic visual interest to hero */
function FloatingMockups({ images = [] }) {
  const positions = [
    { className: 'hidden lg:block absolute top-[14%] left-2 xl:left-6 w-64 xl:w-72 rotate-[-4deg]',  delay: 0 },
    { className: 'hidden lg:block absolute top-[10%] right-2 xl:right-6 w-64 xl:w-72 rotate-[5deg]', delay: 0.2 },
    { className: 'hidden xl:block absolute bottom-[6%] left-12 w-56 rotate-[3deg]',                  delay: 0.4 },
    { className: 'hidden xl:block absolute bottom-[10%] right-12 w-56 rotate-[-4deg]',               delay: 0.6 },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none">
      {images.slice(0, positions.length).map((src, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: positions[i].delay, ease: [0.25, 0.46, 0.45, 0.94] }}
          className={positions[i].className}
        >
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 6 + i, repeat: Infinity, ease: 'easeInOut' }}
            className="relative rounded-xl overflow-hidden bg-[#0f172a]
                       ring-1 ring-[#d4a84b]/40 shadow-[0_25px_50px_-15px_rgba(0,0,0,0.8),_0_0_40px_-15px_rgba(212,168,75,0.5)]"
          >
            {/* Browser-style top bar */}
            <div className="flex items-center gap-1.5 px-3 py-2 bg-[#0a0e1a] border-b border-white/5">
              <span className="w-2 h-2 rounded-full bg-[#ef4444]" />
              <span className="w-2 h-2 rounded-full bg-[#f59e0b]" />
              <span className="w-2 h-2 rounded-full bg-[#10b981]" />
            </div>
            <img src={src} alt="" className="w-full h-auto block" loading="lazy" />
            {/* Subtle gold inner glow */}
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-transparent to-[#d4a84b]/10 pointer-events-none" />
          </motion.div>
        </motion.div>
      ))}
    </div>
  );
}

function Reveal({ children, delay = 0, className = '' }) {
  return <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, delay, ease: [0.25, 0.46, 0.45, 0.94] }} className={className}>{children}</motion.div>;
}

/* Dynamic BG Rotator */
function DubaiBg({ images, opacity = 0.06 }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => { const t = setInterval(() => setIdx(i => (i + 1) % images.length), 6000); return () => clearInterval(t); }, [images.length]);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {images.map((src, i) => (
        <div key={i} className="absolute inset-0 bg-cover bg-center transition-opacity duration-[2000ms]" style={{ backgroundImage: `url(${src})`, opacity: i === idx ? opacity : 0 }} />
      ))}
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [demoLoading, setDemoLoading] = useState(false);

  const handleDemo = async () => {
    setDemoLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/demo-login`, { method: 'POST' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      localStorage.setItem('erp_token', data.access_token);
      localStorage.setItem('erp_user', JSON.stringify({ ...data.user, is_demo: true }));
      localStorage.setItem('erp_demo_mode', 'true');
      window.location.href = '/dashboard';
    } catch { alert('Demo unavailable.'); }
    finally { setDemoLoading(false); }
  };

  return (
    <div className="bg-[#0a0e1a] text-zinc-100 min-h-screen" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <MarketingNav />

      {/* HERO */}
      <section className="relative pt-28 pb-20 lg:pt-40 lg:pb-32 overflow-hidden" data-testid="hero-section">
        <DubaiBg images={DUBAI_BG} opacity={0.18} />
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0e1a]/60 via-[#0a0e1a]/85 to-[#0a0e1a] pointer-events-none" />
        {/* Glowing accent orbs */}
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#d4a84b]/10 blur-[120px] pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#1e3a5f]/30 blur-[120px] pointer-events-none" />
        {/* Premium gold accent line */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#d4a84b] to-transparent opacity-80" />
        {/* Floating dynamic screen mockups (decorative) */}
        <FloatingMockups images={HERO_DYNAMIC_SHOTS} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center max-w-4xl mx-auto">
            {/* Audience badge */}
            <div className="inline-flex items-center gap-2 bg-[#d4a84b]/10 border border-[#d4a84b]/30 rounded-full px-4 py-1.5 mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
              <span className="text-xs font-semibold text-[#d4a84b]">Trusted by Accountants, Auditors & SMEs across the UAE</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] mb-6 text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Run Your Entire<br />
              Business From<br />
              <span className="bg-gradient-to-r from-[#d4a84b] via-[#f6d275] to-[#d4a84b] bg-clip-text text-transparent">One Platform</span>
            </h1>

            <p className="text-base lg:text-lg leading-relaxed text-zinc-300 max-w-2xl mx-auto mb-4">
              BizCore&trade; is the all-in-one cloud ERP built for SMEs and professional firms. 
              HR, Accounting, Sales, Purchases, VAT &amp; GST Compliance, and Analytics — user-friendly, non-complicated entries with full audit trails.
            </p>

            {/* Target audience callout */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
              <div className="inline-flex items-center gap-2 bg-[#d4a84b]/15 border border-[#d4a84b]/30 rounded-full px-5 py-2 backdrop-blur-sm">
                <Briefcase size={14} className="text-[#d4a84b]" />
                <span className="text-sm font-semibold text-zinc-100">
                  Suitable for Auditors, Accountants managing Multiple Companies & SMEs
                </span>
              </div>
              <div className="inline-flex items-center gap-2 bg-[#10B981]/15 border border-[#10B981]/30 rounded-full px-4 py-2 backdrop-blur-sm">
                <Shield size={14} className="text-[#10B981]" />
                <span className="text-sm font-semibold text-zinc-100">
                  VAT & GST Compliance Ready
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              <button onClick={handleDemo} disabled={demoLoading}
                className="bg-[#d4a84b] text-[#0a0e1a] hover:bg-[#e8bc5e] rounded-lg font-bold px-8 py-3.5 text-sm transition-all flex items-center gap-2 shadow-[0_8px_24px_-8px_rgba(212,168,75,0.6)] hover:shadow-[0_12px_28px_-6px_rgba(212,168,75,0.7)] hover:-translate-y-0.5" data-testid="hero-demo-btn">
                <Play size={16} fill="currentColor" />{demoLoading ? 'Starting...' : 'Try Live Demo — Free'}
              </button>
              <Link to="/features" className="bg-white/5 text-zinc-100 border border-white/15 hover:bg-white/10 hover:border-[#d4a84b]/40 rounded-lg font-semibold px-8 py-3.5 text-sm transition-colors flex items-center gap-2 backdrop-blur-sm">
                Explore Features <ChevronRight size={16} />
              </Link>
            </div>
          </motion.div>

          {/* Counters */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl mx-auto bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-2xl p-6 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.4)]">
            <Counter end={60} suffix="+" label="Features" />
            <Counter end={12} suffix="+" label="Modules" />
            <Counter end={100} suffix="%" label="Data Isolation" />
            <Counter end={49} suffix="" label="Manual Pages" />
          </motion.div>
        </div>
      </section>

      {/* BUILT FOR REAL BUSINESSES — real-world photos */}
      <section className="relative py-20 lg:py-24 bg-[#0a0e1a] overflow-hidden" data-testid="real-businesses-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d4a84b] mb-3">Built for Real Businesses</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4 text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Trusted by Accountants, Auditors &amp; SMEs Daily
              </h2>
              <p className="text-base leading-relaxed text-zinc-400">
                From solo accountants managing dozens of client books, to growing SMEs running their entire operation — BizCore is what real teams reach for every morning.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* Big feature photo: accountant at laptop */}
            <Reveal delay={0.1} className="md:col-span-7">
              <div className="relative rounded-2xl overflow-hidden group ring-1 ring-white/10 hover:ring-[#d4a84b]/40 transition-all">
                <img
                  src="https://images.unsplash.com/photo-1573164713988-8665fc963095?w=1400&q=70&auto=format&fit=crop"
                  alt="Accountant working on a laptop reviewing financial reports"
                  className="w-full h-[420px] object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e1a] via-[#0a0e1a]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-7">
                  <span className="inline-block text-[10px] font-bold tracking-[0.2em] uppercase text-[#d4a84b] mb-2">Accountants &amp; Auditors</span>
                  <h3 className="text-2xl lg:text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Run multi-company books from one secure workspace
                  </h3>
                  <p className="text-sm text-zinc-300 max-w-xl">
                    Switch between unlimited client companies in one click. Full data isolation, audit trails, and bulk VAT/GST filing — everything a practice needs.
                  </p>
                </div>
              </div>
            </Reveal>

            {/* Right column: 2 stacked photos */}
            <Reveal delay={0.2} className="md:col-span-5 grid grid-rows-2 gap-4">
              <div className="relative rounded-2xl overflow-hidden group ring-1 ring-white/10 hover:ring-[#d4a84b]/40 transition-all h-[202px]">
                <img
                  src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=900&q=70&auto=format&fit=crop"
                  alt="Business team reviewing P&L reports"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e1a] via-[#0a0e1a]/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="inline-block text-[10px] font-bold tracking-[0.2em] uppercase text-[#10b981] mb-1">SME Owners</span>
                  <h4 className="text-base font-bold text-white">Real-time P&amp;L &amp; cash flow</h4>
                  <p className="text-xs text-zinc-300">Know your numbers without waiting for month-end.</p>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden group ring-1 ring-white/10 hover:ring-[#d4a84b]/40 transition-all h-[202px]">
                <img
                  src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=900&q=70&auto=format&fit=crop"
                  alt="Auditor preparing VAT/GST tax compliance reports"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e1a] via-[#0a0e1a]/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="inline-block text-[10px] font-bold tracking-[0.2em] uppercase text-[#3b82f6] mb-1">Tax Compliance</span>
                  <h4 className="text-base font-bold text-white">VAT &amp; GST, file-ready</h4>
                  <p className="text-xs text-zinc-300">UAE VAT returns &amp; Indian GSTR exports, on-tap.</p>
                </div>
              </div>
            </Reveal>

            {/* Bottom row: 3 photos */}
            <Reveal delay={0.35} className="md:col-span-4">
              <div className="relative rounded-2xl overflow-hidden group ring-1 ring-white/10 hover:ring-[#d4a84b]/40 transition-all h-[230px]">
                <img
                  src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=900&q=70&auto=format&fit=crop"
                  alt="Sales team preparing quotations"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e1a] via-[#0a0e1a]/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="inline-block text-[10px] font-bold tracking-[0.2em] uppercase text-[#f59e0b] mb-1">Sales Teams</span>
                  <h4 className="text-base font-bold text-white">Quotes &amp; invoices in minutes</h4>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.4} className="md:col-span-4">
              <div className="relative rounded-2xl overflow-hidden group ring-1 ring-white/10 hover:ring-[#d4a84b]/40 transition-all h-[230px]">
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=900&q=70&auto=format&fit=crop"
                  alt="HR manager processing payroll"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e1a] via-[#0a0e1a]/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="inline-block text-[10px] font-bold tracking-[0.2em] uppercase text-[#ec4899] mb-1">HR &amp; Payroll</span>
                  <h4 className="text-base font-bold text-white">Run payroll WPS-compliant</h4>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.45} className="md:col-span-4">
              <div className="relative rounded-2xl overflow-hidden group ring-1 ring-white/10 hover:ring-[#d4a84b]/40 transition-all h-[230px]">
                <img
                  src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=900&q=70&auto=format&fit=crop"
                  alt="Procurement professional managing purchase orders"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e1a] via-[#0a0e1a]/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="inline-block text-[10px] font-bold tracking-[0.2em] uppercase text-[#8b5cf6] mb-1">Procurement</span>
                  <h4 className="text-base font-bold text-white">POs, RFQs &amp; GRNs</h4>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TWO EDITIONS SECTION */}
      <section className="relative py-20 lg:py-28 bg-[#0d1220] overflow-hidden" data-testid="editions-section">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(212,168,75,0.04)_25%,transparent_25%,transparent_50%,rgba(212,168,75,0.04)_50%,rgba(212,168,75,0.04)_75%,transparent_75%,transparent)] bg-[length:20px_20px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-14">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d4a84b] mb-3">Two Editions, One Platform</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4 text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Choose the Edition That Fits Your Business
              </h2>
              <p className="text-base leading-relaxed text-zinc-400">
                Whether you're running a single SME or managing multiple client portfolios as an Accountant or Auditor — BizCore has an edition purpose-built for you.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* BizCore SME Edition */}
            <Reveal delay={0.1}>
              <div className="relative bg-[#11192b] border-2 border-white/10 rounded-2xl overflow-hidden hover:border-[#1e3a5f]/80 hover:shadow-2xl transition-all duration-300 h-full flex flex-col" data-testid="edition-bizcore">
                {/* Hero Logo */}
                <div className="bg-[#0a0e1a] px-8 py-8 flex items-center justify-center border-b border-white/5">
                  <img src="/logos/bizcore-hero.png" alt="BizCore - SME Edition" className="h-20 sm:h-24 w-auto object-contain" />
                </div>
                <div className="p-8 lg:p-10 flex flex-col flex-1">
                  <div className="mb-1">
                    <p className="text-xs font-bold uppercase tracking-widest text-[#3b82f6] mb-1">SME Edition</p>
                    <p className="text-sm leading-relaxed text-zinc-400">
                      The complete ERP for small and medium enterprises. Manage your entire business operations — HR, accounting, sales, purchases, tax compliance — from one clean, user-friendly interface.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-6 mb-8 flex-1">
                    {[
                      'HR & Payroll Management',
                      'Sales & Invoicing with QR',
                      'Purchase Orders & RFQs',
                      'Chart of Accounts & Ledger',
                      'UAE VAT & Indian GST',
                      'Personalized Reports & Dashboards',
                      'Digital Signature on Documents',
                      'Approval Workflows',
                      'Document Storage & Calendar',
                      'GSTR Report Generation',
                      'Attendance & Leave System',
                      'Budget & Cost Control',
                    ].map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 size={14} className="text-[#10B981] shrink-0" /><span className="text-zinc-300">{f}</span>
                      </div>
                    ))}
                  </div>
                  <Link to="/features" className="inline-flex items-center gap-2 text-sm font-semibold text-[#3b82f6] hover:text-[#60a5fa] transition-colors">
                    View All SME Features <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>

            {/* BizCore Pro Edition */}
            <Reveal delay={0.2}>
              <div className="relative bg-[#0f172a] border-2 border-[#d4a84b]/30 rounded-2xl overflow-hidden hover:border-[#d4a84b]/60 hover:shadow-xl hover:shadow-[#d4a84b]/5 transition-all duration-300 h-full flex flex-col" data-testid="edition-bizcore-pro">
                <div className="absolute top-4 right-4 z-10 bg-[#d4a84b]/15 text-[#d4a84b] text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">Pro</div>
                {/* Hero Logo */}
                <div className="bg-[#0a1120] px-8 py-8 flex items-center justify-center border-b border-[#d4a84b]/10">
                  <img src="/logos/bizcore-pro-hero.png" alt="BizCore Pro - Accountant & Auditor Edition" className="h-20 sm:h-24 w-auto object-contain" />
                </div>
                <div className="p-8 lg:p-10 flex flex-col flex-1">
                  <div className="mb-1">
                    <p className="text-xs font-bold uppercase tracking-widest text-[#d4a84b] mb-1">Accountant & Auditor Edition</p>
                    <p className="text-sm leading-relaxed text-zinc-400">
                      Purpose-built for accountants & auditors managing multiple company accounts. One login, unlimited clients, complete data isolation, VAT & GST filing, Corporate Tax compliance across all portfolios.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-6 mb-8 flex-1">
                  {[
                    'All BizCore SME Features',
                    'Multi-Client Company Management',
                    'One-Click Context Switching',
                    'Bulk VAT & GST Reports',
                    'Corporate Tax Compliance',
                    'Personalized Dashboards per Client',
                    'Cross-Client Analytics',
                    'Client Task Management',
                    'Scheduled Backup per Client',
                    'Multi-Currency Support',
                    'Compliance Overview Reports',
                    'Accountant Hub Dashboard',
                  ].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 size={14} className="text-[#d4a84b] shrink-0" /><span className="text-zinc-300">{f}</span>
                    </div>
                  ))}
                </div>
                <Link to="/features" className="inline-flex items-center gap-2 text-sm font-semibold text-[#d4a84b] hover:text-[#e8bc5e] transition-colors">
                  View All Pro Features <ArrowRight size={15} />
                </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY BIZCORE */}
      <section className="relative py-20 lg:py-28 overflow-hidden bg-[#0a0e1a]" data-testid="why-section">
        <DubaiBg images={[DUBAI_BG[1]]} opacity={0.10} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0e1a] via-transparent to-[#0a0e1a] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-14">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d4a84b] mb-3">Why Choose BizCore</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4 text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Not Just Another ERP. Built Different.
              </h2>
              <p className="text-base leading-relaxed text-zinc-400">
                Most ERPs are overcomplicated, expensive, and built for enterprises. BizCore strips away the bloat — 
                giving you powerful, user-friendly tools that just work. No consultants required.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Building2, title: 'Multi-Company Accounts', desc: 'Accountants & Auditors manage unlimited client companies. One login, complete data isolation, instant context switching.', accent: '#d4a84b' },
              { icon: Landmark, title: 'VAT, GST & Corporate Tax Ready', desc: 'UAE VAT return preparation, Corporate Tax compliance, Indian GST support including GSTR reports. Tax-ready from day one — no add-ons needed.', accent: '#10B981' },
              { icon: PieChart, title: 'Personalized Reports & Dashboards', desc: 'Revenue trends, expense breakdown, profit margins, cash flow projections. Interactive dashboards with smart AI-powered insights.', accent: '#3b82f6' },
              { icon: QrCode, title: 'QR Code Verification', desc: 'Every document embeds a tamper-proof QR code. Clients and auditors verify authenticity instantly via a branded landing page.', accent: '#8b5cf6' },
              { icon: Globe, title: 'Multi-Currency Engine', desc: 'AED, USD, INR, EUR — automatic exchange rates. Invoices, reports, and dashboards adapt to any base currency.', accent: '#f59e0b' },
              { icon: ClipboardCheck, title: 'Approval Workflows', desc: 'Multi-role approval chains. Nothing gets sent without the right sign-off. Editable documents with full version tracking.', accent: '#ec4899' },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl p-7 hover:border-[#d4a84b]/30 hover:bg-white/[0.05] hover:-translate-y-0.5 transition-all duration-300 h-full">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${item.accent}25`, boxShadow: `0 0 20px -5px ${item.accent}40` }}>
                    <item.icon size={22} style={{ color: item.accent }} />
                  </div>
                  <h3 className="text-base font-bold mb-2 text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                  <p className="text-sm leading-relaxed text-zinc-400">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Keywords strip */}
          <Reveal>
            <div className="mt-14 flex flex-wrap justify-center gap-2">
              {['User Friendly', 'Non-Complicated Entries', 'Full Audit Logs', 'Editable Documents', 'Accountants & Auditors', 'SME Focused', 'VAT & GST Ready', 'Multi-Company', 'UAE & India Compliant', 'Personalized Dashboards'].map(kw => (
                <span key={kw} className="text-xs font-medium px-3 py-1.5 rounded-full bg-white/[0.04] text-zinc-300 border border-white/10 backdrop-blur-sm hover:border-[#d4a84b]/40 hover:text-[#d4a84b] transition-colors">{kw}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ACCOUNTANTS & AUDITORS HIGHLIGHT */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-[#0f172a] to-[#162544] overflow-hidden" data-testid="accountant-section">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(212,168,75,0.06)_0%,transparent_60%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <div>
                <div className="inline-flex items-center gap-2 bg-[#d4a84b]/10 border border-[#d4a84b]/20 rounded-full px-4 py-1.5 mb-5">
                  <Target size={14} className="text-[#d4a84b]" />
                  <span className="text-xs font-bold uppercase tracking-wider text-[#d4a84b]">For Accountants & Auditors</span>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Manage Multiple Companies from a Single Dashboard
                </h2>
                <p className="text-base leading-relaxed text-zinc-400 mb-8">
                  BizCore Pro&trade; is designed specifically for accounting and auditing firms managing multiple SME clients. 
                  Complete data isolation, bulk VAT & GST report generation, and cross-client compliance tracking — all from one login.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                  {[
                    'Unlimited Client Companies',
                    'One-Click Context Switching',
                    'Personalized Reports per Client',
                    'Bulk VAT & GST Return Prep',
                    'Corporate Tax Calculations',
                    'Compliance Overview Dashboard',
                    'Client Task Assignment',
                    'Scheduled Backups per Client',
                  ].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 size={16} className="text-[#d4a84b] shrink-0" /><span className="text-zinc-300">{f}</span>
                    </div>
                  ))}
                </div>
                <button onClick={handleDemo} disabled={demoLoading}
                  className="bg-[#d4a84b] text-[#0f172a] hover:bg-[#e8bc5e] rounded-lg font-semibold px-6 py-3 text-sm transition-all flex items-center gap-2 shadow-lg" data-testid="accountant-demo-btn">
                  <Play size={15} fill="currentColor" />Try Pro Demo
                </button>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="grid grid-cols-2 gap-4">
                <ScreenshotCardDark src="/booklet/62_accountant_clients_full.jpeg" title="Client Management" />
                <ScreenshotCardDark src="/booklet/67_switch_client.jpeg" title="Context Switching" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* APP PREVIEW SCREENSHOTS */}
      <section className="py-20 lg:py-28 bg-[#0f172a]" data-testid="preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-14">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d4a84b] mb-3">See It In Action</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Every Module, At a Glance
              </h2>
              <p className="text-base leading-relaxed text-zinc-400">Real screenshots from the live app — Sales, Accounts, P&amp;L, Compliance, Payroll and more. Click to enlarge.</p>
            </div>
          </Reveal>
          <FeatureShowcase />
          <div className="text-center mt-10">
            <Link to="/features" className="inline-flex items-center gap-2 text-sm font-semibold text-[#d4a84b] hover:text-white transition-colors">
              View All Features & Screenshots <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* TAX COMPLIANCE HIGHLIGHT */}
      <section className="relative py-20 lg:py-28 overflow-hidden bg-[#0d1220]" data-testid="tax-section">
        <DubaiBg images={[DUBAI_BG[2]]} opacity={0.10} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d1220] via-transparent to-[#0d1220] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#10B981] mb-3">Tax Compliance</p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-6 text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  VAT, Corporate Tax & GST — Built In, Not Bolted On
                </h2>
                <p className="text-base leading-relaxed text-zinc-400 mb-8">
                  BizCore is designed for multi-country tax compliance from the ground up. UAE VAT return preparation, Corporate Tax calculations, 
                  and Indian GST compliance with GSTR report generation are native features — not expensive add-ons.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    'UAE VAT 5% Automated',
                    'Corporate Tax Filing',
                    'Tax Period Management',
                    'Indian GST Compliance',
                    'GSTR Report Generation',
                    'Multi-rate Tax Setup',
                    'Filing History & Audit',
                    'Reverse Charge Support',
                    'GST HSN/SAC Codes',
                    'Tax Return Reports',
                  ].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-zinc-300"><CheckCircle2 size={16} className="text-[#10B981] shrink-0" />{f}</div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <ScreenshotCard src="/booklet/41_tax_compliance.jpeg" title="Tax Compliance Dashboard" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="relative py-16 overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f172a] via-[#1a2744] to-[#0f172a]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(212,168,75,0.08)_0%,transparent_50%)]" />
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#d4a84b]/40 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Ready to Simplify Your Business?
            </h2>
            <p className="text-base text-zinc-300 mb-8 max-w-xl mx-auto">
              Join accountants, auditors, and SMEs across the UAE who trust BizCore for their daily operations.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={handleDemo} disabled={demoLoading} className="bg-[#d4a84b] text-[#0f172a] hover:bg-[#b8912e] rounded-lg font-semibold px-8 py-3.5 text-sm transition-all flex items-center gap-2 shadow-lg">
                <Play size={16} fill="currentColor" />Try Live Demo
              </button>
              <Link to="/pricing" className="bg-white/10 text-white border border-white/20 hover:bg-white/20 rounded-lg font-semibold px-8 py-3.5 text-sm transition-colors flex items-center gap-2">
                View Pricing <ChevronRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

/* Screenshot Components */
function ScreenshotCard({ src, title }) {
  const [lightbox, setLightbox] = useState(false);
  return (
    <>
      <div className="group cursor-pointer rounded-2xl overflow-hidden border border-white/10 shadow-2xl hover:border-[#d4a84b]/40 hover:shadow-[#d4a84b]/10 transition-all bg-[#11192b]" onClick={() => setLightbox(true)}>
        <img src={src} alt={title} className="w-full h-auto transition-transform group-hover:scale-[1.02]" loading="lazy" />
        <div className="px-4 py-3 bg-[#11192b] border-t border-white/5 flex items-center justify-between">
          <span className="text-sm font-medium text-zinc-200">{title}</span>
          <span className="text-xs text-zinc-500">Click to enlarge</span>
        </div>
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-pointer" onClick={() => setLightbox(false)} data-testid="screenshot-lightbox">
          <img src={src} alt={title} className="max-w-full max-h-[90vh] rounded-lg shadow-2xl" />
          <button className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors text-xl">&times;</button>
        </div>
      )}
    </>
  );
}

function FeatureShowcase() {
  const features = [
    { src: '/booklet/02_dashboard.jpeg',    title: 'Live Dashboard',         tag: 'OVERVIEW',   accent: '#d4a84b', desc: 'KPI cards, revenue trends, cash-flow at a glance.' },
    { src: '/booklet/06_sales.jpeg',         title: 'Sales & Quotations',    tag: 'SALES',      accent: '#3b82f6', desc: 'Quotes → Invoices, revisions, costing, QR PDFs.' },
    { src: '/booklet/16_invoice_pdf_qr.jpeg', title: 'Invoice PDF with QR',   tag: 'INVOICING',  accent: '#10b981', desc: 'Branded, tamper-proof tax invoices with one click.' },
    { src: '/booklet/18_accounts_coa.jpeg',  title: 'Chart of Accounts',     tag: 'ACCOUNTS',   accent: '#8b5cf6', desc: 'Multi-level CoA, ledgers, journal entries, audit trail.' },
    { src: '/booklet/35_trial_balance.jpeg', title: 'Trial Balance & P&L',   tag: 'REPORTS',    accent: '#f59e0b', desc: 'P&L, Balance Sheet, Trial Balance — instantly.' },
    { src: '/booklet/21_analytics_charts.jpeg', title: 'Analytics & Charts',  tag: 'INSIGHTS',   accent: '#ec4899', desc: 'Revenue, expense, margin breakdowns by period.' },
    { src: '/booklet/22_analytics_forecast.jpeg', title: 'Forecasting',      tag: 'INSIGHTS',   accent: '#06b6d4', desc: 'AI-assisted cash-flow & revenue forecasting.' },
    { src: '/booklet/26_compliance.jpeg',    title: 'VAT & GST Compliance',  tag: 'COMPLIANCE', accent: '#22c55e', desc: 'UAE VAT returns, Indian GSTR reports, ready to file.' },
    { src: '/booklet/04_payroll.jpeg',       title: 'Payroll & Salaries',    tag: 'HR',         accent: '#84cc16', desc: 'WPS-compliant salary runs, payslips, deductions.' },
    { src: '/booklet/19_purchases_po.jpeg',  title: 'Purchases & POs',       tag: 'PROCUREMENT',accent: '#f97316', desc: 'RFQs, POs, GRNs with approval workflows.' },
    { src: '/booklet/34_journal_entries.jpeg', title: 'Journal Entries',      tag: 'ACCOUNTS',   accent: '#a855f7', desc: 'Double-entry bookkeeping with audit history.' },
    { src: '/booklet/12_accountant.jpeg',    title: 'Accountant Hub',        tag: 'MULTI-CO',   accent: '#0ea5e9', desc: 'Manage many clients from one secure workspace.' },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {features.map((f, i) => (
        <Reveal key={i} delay={(i % 4) * 0.06}>
          <FeatureScreenshotCard {...f} />
        </Reveal>
      ))}
    </div>
  );
}

function FeatureScreenshotCard({ src, title, tag, accent, desc }) {
  const [lightbox, setLightbox] = useState(false);
  return (
    <>
      <div
        className="group cursor-pointer rounded-xl overflow-hidden border border-white/10 bg-[#11192b] hover:border-[#d4a84b]/50 hover:-translate-y-1 transition-all duration-300 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)] hover:shadow-[0_20px_40px_-15px_rgba(212,168,75,0.25)]"
        onClick={() => setLightbox(true)}
        data-testid={`feature-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      >
        <div className="relative overflow-hidden">
          <img src={src} alt={title} className="w-full h-44 object-cover object-top transition-transform duration-500 group-hover:scale-105" loading="lazy" />
          <span
            className="absolute top-2 left-2 text-[10px] font-bold px-2 py-1 rounded-full backdrop-blur-md"
            style={{ background: `${accent}26`, color: accent, border: `1px solid ${accent}55` }}
          >{tag}</span>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e1a]/80 via-transparent to-transparent" />
        </div>
        <div className="p-4">
          <h3 className="text-base font-bold text-white mb-1 group-hover:text-[#d4a84b] transition-colors">{title}</h3>
          <p className="text-xs leading-relaxed text-zinc-400">{desc}</p>
        </div>
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-pointer" onClick={() => setLightbox(false)} data-testid="feature-lightbox">
          <img src={src} alt={title} className="max-w-full max-h-[90vh] rounded-lg shadow-2xl" />
          <button className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 text-xl">&times;</button>
        </div>
      )}
    </>
  );
}

function AppScreenshots() {
  const screenshots = [
    { src: '/booklet/02_dashboard.jpeg', title: 'Dashboard & KPIs' },
    { src: '/booklet/09_analytics.jpeg', title: 'Analytics & Insights' },
    { src: '/booklet/06_sales.jpeg', title: 'Sales & Invoicing' },
    { src: '/booklet/16_invoice_pdf_qr.jpeg', title: 'Invoice PDF with QR' },
    { src: '/booklet/18_accounts_coa.jpeg', title: 'Chart of Accounts' },
    { src: '/booklet/03_hr.jpeg', title: 'HR Management' },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {screenshots.map((s, i) => (
        <Reveal key={i} delay={i * 0.08}>
          <ScreenshotCardDark {...s} />
        </Reveal>
      ))}
    </div>
  );
}

function ScreenshotCardDark({ src, title }) {
  const [lightbox, setLightbox] = useState(false);
  return (
    <>
      <div className="group cursor-pointer rounded-xl overflow-hidden border border-zinc-700/50 hover:border-[#d4a84b]/40 transition-all" onClick={() => setLightbox(true)} data-testid={`screenshot-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <img src={src} alt={title} className="w-full h-auto transition-transform group-hover:scale-[1.02]" loading="lazy" />
        <div className="px-4 py-2.5 bg-zinc-900/80 flex items-center justify-between">
          <span className="text-sm font-medium text-zinc-200">{title}</span>
          <span className="text-[10px] text-zinc-500">Click to expand</span>
        </div>
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-pointer" onClick={() => setLightbox(false)} data-testid="screenshot-lightbox">
          <img src={src} alt={title} className="max-w-full max-h-[90vh] rounded-lg shadow-2xl" />
          <button className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 text-xl">&times;</button>
        </div>
      )}
    </>
  );
}
